import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getTestUsers, deleteTestUsers } from '../../services/api';

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

const SORTABLE_COLUMNS = [
  { key: 'username', label: 'Username' },
  { key: 'email', label: 'Email' },
  { key: 'date_joined', label: 'Created' },
  { key: 'last_login', label: 'Last Login' },
  { key: 'contracts_count', label: 'Contracts' },
  { key: 'groups_count', label: 'Groups' },
];

function TestUserCleanup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'date_joined', dir: 'desc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getTestUsers();
      setData(result);
      setSelectedIds(new Set());
      setConfirmDelete(false);
    } catch (err) {
      setError(err.message || 'Failed to fetch test users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sortedUsers = useMemo(() => {
    if (!data?.test_users) return [];
    const sorted = [...data.test_users];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.dir === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      if (sortConfig.dir === 'asc') return (aVal || 0) - (bVal || 0);
      return (bVal || 0) - (aVal || 0);
    });
    return sorted;
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setConfirmDelete(false);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedUsers.map((u) => u.id)));
    }
    setConfirmDelete(false);
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await deleteTestUsers([...selectedIds]);
      setConfirmDelete(false);
      await fetchData();
    } catch (err) {
      setError(`Failed to delete users: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
        Loading test users...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '6px',
          color: '#721c24',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button
            onClick={fetchData}
            style={{
              padding: '4px 12px',
              backgroundColor: 'transparent',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              color: '#dc3545',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const users = sortedUsers;

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#2c3e50', fontSize: '20px', fontWeight: '600', margin: 0 }}>
          Test User Cleanup
        </h2>
        <button
          onClick={fetchData}
          style={{
            padding: '6px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            color: '#6c757d',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <p style={{ color: '#6c757d', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>
            Test Users Found
          </p>
          <p style={{ color: '#2c3e50', fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {summary.total || 0}
          </p>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <p style={{ color: '#6c757d', fontSize: '12px', marginBottom: '4px', textTransform: 'uppercase' }}>
            Selected for Deletion
          </p>
          <p style={{ color: selectedIds.size > 0 ? '#dc3545' : '#2c3e50', fontSize: '28px', fontWeight: '700', margin: 0 }}>
            {selectedIds.size}
          </p>
        </div>
      </div>

      {/* Delete action bar */}
      {selectedIds.size > 0 && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff5f5',
          border: '1px solid #f5c6cb',
          borderRadius: '6px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: '#721c24', fontSize: '14px' }}>
            {selectedIds.size} user{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          {confirmDelete ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {deleting ? 'Deleting...' : `Confirm Delete ${selectedIds.size}`}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{
                  padding: '6px 16px',
                  backgroundColor: 'transparent',
                  color: '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              style={{
                padding: '6px 16px',
                backgroundColor: 'transparent',
                color: '#dc3545',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Delete Selected
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {users.length === 0 ? (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          color: '#6c757d',
        }}>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>No orphaned test users found</p>
          <p style={{ fontSize: '13px' }}>Test users matching known patterns will appear here.</p>
        </div>
      ) : (
        /* Table */
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #dee2e6',
                  textAlign: 'center',
                  width: '40px',
                }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === users.length && users.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                {SORTABLE_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      color: sortConfig.key === col.key ? '#007bff' : '#495057',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      borderBottom: '1px solid #dee2e6',
                      userSelect: 'none',
                    }}
                  >
                    {col.label}
                    {sortConfig.key === col.key && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.dir === 'asc' ? '\u25B2' : '\u25BC'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  style={{
                    borderBottom: '1px solid #e9ecef',
                    backgroundColor: selectedIds.has(user.id) ? '#fff5f5' : 'transparent',
                  }}
                >
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={() => toggleSelect(user.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', color: '#2c3e50', fontWeight: '500' }}>
                    {user.username}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6c757d' }}>
                    {user.email || '-'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                    {formatDate(user.date_joined)}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                    {formatTimeAgo(user.last_login)}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#495057', textAlign: 'center' }}>
                    {user.contracts_count || 0}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#495057', textAlign: 'center' }}>
                    {user.groups_count || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TestUserCleanup;
